import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//assets
import ShipperGuideLogo from '@/public/images/home/logos/shipperguide.svg';
import OpendockLogo from '@/public/images/home/logos/opendock.svg';

//components
import {
  Section,
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
} from '@/containers-ai/SectionHeading';

import {
  PlatformScale,
  platformScaleStats,
} from '@/containers-ai/PlatformScale';

//styles
import {
  RailWrap,
  Manager,
  ManagerDot,
  Rail,
  Progress,
  Stage,
  StageNode,
  StageName,
  RailCaps,
  RailCap,
  StagePanel,
  StageName2,
  StageProductTag,
  StageList,
  StageListItem,
  StageCard,
  StageCardStatus,
  StageCardTitle,
  StageCardMeta,
  StageCardValue,
} from './LifecycleRail.styles';
import { Container } from '@/components/Grid';

const stages = [
  {
    name: 'Planning',
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
    list: [
      'Consolidates LTL into fewer, fuller loads',
      'Turns a working set of orders into a planned set of shipments',
      'Picks the mode for cost and service',
    ],
    event: {
      title: '3 LTL orders consolidated into one truckload',
      agent: 'Scheduled planning',
      ref: 'ShipperGuide · plan WK-29',
      value: '$1,240 saved',
    },
  },
  {
    name: 'Procurement',
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
    list: [
      'Tenders down your routing guide against your rules',
      "Holds when a rate is over benchmark, takes spot when it's cheaper",
      'Books the carrier and confirms the window',
    ],
    event: {
      title: 'Tendered down the routing guide, spot taken under benchmark',
      agent: 'Automated tendering',
      ref: 'ShipperGuide · ATL → DFW',
      value: '$2,410 · below target',
    },
  },
  {
    name: 'Execution',
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
    list: [
      'Forward an email — the AI files the doc and creates the charges',
      'Drop in a PO and it builds the shipment',
      'Reads carrier updates and syncs ETAs to your TMS',
    ],
    event: {
      title: 'Emailed rate con filed, charges created in your TMS',
      agent: 'Smart Inbox',
      ref: 'Copilot AI · #LS-48180',
      value: 'written back, no data entry',
    },
  },
  {
    name: 'Settlement & Payments',
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
    list: [
      'Audits every invoice against expected charges',
      'Auto-approves within tolerance, flags the rest',
      'A scheduled agent clears pending charges daily',
    ],
    event: {
      title: 'Off-contract accessorial caught on auto-audit',
      agent: 'Invoice auto-audit',
      ref: 'ShipperGuide · #INV-90211',
      value: '$4,120 flagged',
    },
  },
  {
    name: 'Gate',
    product: 'Opendock',
    productLogo: (
      <OpendockLogo role="img" aria-label="Opendock" width="89" height="13" />
    ),
    facility: true,
    list: [
      'Catalogs unplanned arrivals at the gate automatically',
      'Checks in and validates the driver',
      'Captures asset identifiers at each gate event',
    ],
    event: {
      title: 'Unplanned arrival cataloged and flagged for the yard',
      agent: 'SmartGate',
      ref: 'Opendock · Laredo gate 3',
      value: 'clean arrival data to the schedule',
    },
  },
  {
    name: 'Yard',
    product: 'Opendock',
    productLogo: (
      <OpendockLogo role="img" aria-label="Opendock" width="89" height="13" />
    ),
    facility: true,
    list: [
      'Tracks where every trailer sits right now',
      'Schedules yard moves and tasks when a pickup is scheduled',
      'Keeps gate-to-dock flow visible',
    ],
    event: {
      title: 'Trailer 4471 flagged to door 9 ahead of unload',
      agent: 'Opendock yard',
      ref: 'Savannah DC',
      value: 'door never idle',
    },
  },
  {
    name: 'Dock',
    product: 'Opendock',
    productLogo: (
      <OpendockLogo role="img" aria-label="Opendock" width="89" height="13" />
    ),
    facility: true,
    list: [
      'Books and rebooks appointments in seconds',
      'Captures an eSignature at the dock as proof of pickup or delivery',
      'Flows the final BOL straight into your ERP',
    ],
    event: {
      title: 'Digital BOL captured at the dock, synced to your ERP',
      agent: 'Digital BOL',
      ref: 'Opendock · Columbus DC door 14',
      value: 'proof of delivery, no paper',
    },
  },
];

const AUTO_ADVANCE_MS = 3400;
const RESUME_AFTER_MS = 12000;

export function LifecycleRail() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const { ref: railRef, inView: railInView } = useInView({
    threshold: 0.35,
    triggerOnce: true,
  });
  const autoTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion || !railInView) return undefined;
    autoTimerRef.current = setInterval(() => {
      setActiveIndex((current) => (current + 1) % stages.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(autoTimerRef.current);
  }, [prefersReducedMotion, railInView]);

  useEffect(
    () => () => {
      clearInterval(autoTimerRef.current);
      clearTimeout(resumeTimerRef.current);
    },
    []
  );

  function handleStageClick(index) {
    setActiveIndex(index);
    clearInterval(autoTimerRef.current);
    clearTimeout(resumeTimerRef.current);

    if (prefersReducedMotion) return;

    resumeTimerRef.current = setTimeout(() => {
      autoTimerRef.current = setInterval(() => {
        setActiveIndex((current) => (current + 1) % stages.length);
      }, AUTO_ADVANCE_MS);
    }, RESUME_AFTER_MS);
  }

  const activeStage = stages[activeIndex];
  const progressWidth = ((activeIndex + 0.5) / stages.length) * 100 - 3;

  return (
    <Section id="platform">
      <Container>
        <Kicker>
          <KickerDot />
          The platform
        </Kicker>
        <SectionTitle>
          One platform that runs your complete freight lifecycle.
        </SectionTitle>
        <SectionLede>
          Every other freight AI solution is bolted onto a single tool. It
          optimizes one part of your operation and ignores everything else.
          Loadsmart&rsquo;s Platform sees every stage of the freight lifecycle,
          so Loadsmart AI can execute from start to end.
        </SectionLede>

        <RailWrap ref={railRef}>
          <Manager>
            <ManagerDot />
            Loadsmart Freight Experts resolve the exceptions
          </Manager>

          <Rail>
            <Progress $width={progressWidth} />
            {stages.map((stage, index) => (
              <Stage
                key={stage.name}
                type="button"
                onClick={() => handleStageClick(index)}
              >
                <StageNode
                  $active={index === activeIndex}
                  $facility={stage.facility}
                />
                <StageName $active={index === activeIndex}>
                  {stage.name}
                </StageName>
              </Stage>
            ))}
          </Rail>

          <RailCaps>
            <RailCap href="https://shipperguide.com/" className="sg qlf-btn-5">
              <b>
                <ShipperGuideLogo
                  role="img"
                  aria-label="ShipperGuide"
                  width="117"
                  height="14"
                />
              </b>{' '}
              &mdash; the AI-native TMS <span className="arw">&#8594;</span>
            </RailCap>
            <RailCap href="https://opendock.com/" className="od qlf-btn-6">
              <b>
                <OpendockLogo
                  role="img"
                  aria-label="Opendock"
                  width="89"
                  height="13"
                />
              </b>{' '}
              &mdash; scheduling, gate &amp; yard{' '}
              <span className="arw">&#8594;</span>
            </RailCap>
          </RailCaps>

          <StagePanel>
            <div>
              <StageName2>
                {activeStage.name}
                <StageProductTag>{activeStage.productLogo}</StageProductTag>
              </StageName2>
              <StageList>
                {activeStage.list.map((item) => (
                  <StageListItem key={item}>{item}</StageListItem>
                ))}
              </StageList>
            </div>
            <StageCard>
              <StageCardStatus>Resolved</StageCardStatus>
              <StageCardTitle>{activeStage.event.title}</StageCardTitle>
              <StageCardMeta>
                <b>{activeStage.event.agent}</b> &middot;{' '}
                {activeStage.event.ref}
              </StageCardMeta>
              <StageCardValue>{activeStage.event.value}</StageCardValue>
            </StageCard>
          </StagePanel>
        </RailWrap>
        <PlatformScale stats={platformScaleStats} />
      </Container>
    </Section>
  );
}
